import { ref, getDownloadURL, uploadString } from "firebase/storage";
import { storage } from "../firebase";

const STORAGE_PATH = "content/landing.json";
const CACHE_KEY = "tx_content_cache_v1";
const CACHE_TTL_MS = 5 * 60 * 1000;

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { data, savedAt } = JSON.parse(raw);
    if (Date.now() - savedAt > CACHE_TTL_MS) return null;
    return isValidShape(data) ? data : null;
  } catch {
    return null;
  }
}

function writeCache(data) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ data, savedAt: Date.now() }));
  } catch { /* localStorage full or disabled — ignore */ }
}

function clearCache() {
  try { localStorage.removeItem(CACHE_KEY); } catch { /* ignore */ }
}

export const DEFAULT_CONTENT = {
  hero: {
    tagline: "",
    headlinePart1: "SEND.",
    headlinePart2: "FLY. EARN.",
    subtextDesktop:
      "Send urgent documents with a verified traveller on your route. Or earn on a flight you're already taking.",
    subtextMobile:
      "Send urgent documents with a verified traveller on your route. Or earn on a flight you're already taking.",
  },
  pricing: {
    senderFee: 0,
    pilotLabel: "First delivery free",
    travellerEarnMin: 200,
    travellerEarnMax: 800,
  },
  cities: ["Mumbai", "Delhi", "Bangalore", "Pune", "Kolkata"],
  stats: [
    { label: "Cities in pilot", prefix: "", num: 5, suffix: "" },
    { label: "Matches hand-reviewed", prefix: "", num: 100, suffix: "%" },
    { label: "ID & flight verified", prefix: "", num: 100, suffix: "%" },
  ],
  // Two banner slots so a traveller offer and a sender offer can run at the
  // same time. audience: "traveller" | "sender" | "both" — the landing page
  // shows a slot only while the hero's role toggle matches it.
  banner: {
    active: false,
    text: "We're in pilot phase across 5 cities. Every match is manually reviewed by our team.",
    link: "",
    style: "info",
    audience: "both",
    // Optional shorter copy for phones, where the banner is one line next to
    // the audience tag. Empty = use text everywhere.
    textShort: "",
  },
  banner2: {
    active: false,
    text: "",
    link: "",
    style: "promo",
    audience: "sender",
    textShort: "",
  },
  testimonials: [
    {
      name: "Murari Prasad",
      city: "Kolkata → Pune",
      time: "₹500 earned",
      text: "It was great and hassle free ♥️",
      role: "Traveller",
      stars: 5,
    },
    {
      name: "Priya K.",
      city: "Delhi → Mumbai",
      time: "4 hrs end-to-end",
      text: "Sent my visa documents at 8am, they were in Mumbai by noon. Got a confirmation message the minute they arrived. Genuinely couldn't believe it.",
      role: "Sender",
      stars: 5,
    },
    {
      name: "Rajan S.",
      city: "Bangalore → Pune",
      time: "₹400 earned",
      text: "Was already flying that route. Got matched, carried a small envelope, earned ₹400. Literally 5 minutes of extra effort. Did it again the following week.",
      role: "Traveller",
      stars: 5,
    },
    {
      name: "Meera T.",
      city: "Mumbai → Kolkata",
      time: "Same day",
      text: "Legal papers that needed to reach by EOD. Courier said 2 days minimum. TurantX delivered same day. I've used it three times since.",
      role: "Sender",
      stars: 5,
    },
  ],
  faqs: [], /* legacy — kept for admin form compatibility; landing uses faqsSender/faqsTraveller */
  faqsSender: [
    {
      q: "How fast will my document reach?",
      a: "Same day, in most cases. Your document travels on a real flight with a verified traveller, so it reaches the destination city within hours of landing — instead of the 2+ days a courier needs. The exact timing depends on the flight you are matched with.",
    },
    {
      q: "How does it work for me?",
      a: "Post your pickup and destination cities along with a short note about your document. Our team reviews your request and matches you with a verified traveller on your route. You hand the document to the traveller before their flight (first mile), and your receiver collects it directly from the traveller after landing (last mile). The traveller's role is simply to carry the document during the flight.",
    },
    {
      q: "Is my first delivery really free?",
      a: "Yes. Your first delivery is free. From your second delivery onwards a delivery fee may apply — we will tell you the amount before the delivery is arranged. If we can't find a verified traveller on your route, your request simply closes and nothing is charged.",
    },
    {
      q: "Why can I trust TurantX?",
      a: "During our pilot phase, every delivery is personally reviewed and coordinated by the TurantX team—you are never matched by an algorithm alone. Every traveller's flight ticket and travel details are verified before a match is confirmed. At handover, you verify the traveller's boarding pass against the details shared by us and confirm the handover using a secure OTP. Before boarding, airlines and airport security also verify the traveller's identity against their boarding pass as part of the normal airport process. Delivery is completed only after your receiver confirms it with a second OTP.",
    },
    {
      q: "What can I send, and what is not allowed?",
      a: "During the pilot, we accept only small documents such as passports, contracts, medical reports, cheques, and legal papers. Cash, jewellery, electronics, perishables, and anything prohibited by airline or government regulations are not accepted.",
    },
    {
      q: "Is this allowed on flights?",
      a: "Yes. Your document travels as part of the traveller's own baggage — fully within airline and government rules, since deliveries are strictly limited to documents during the pilot. Our team reviews every request's details and rejects anything that doesn't comply with regulations, and the traveller personally sees the document at handover before accepting it — nothing travels unseen. At the airport, it simply goes through the same standard security screening as any passenger's belongings.",
    },
    {
      q: "Who handles pickup and drop?",
      a: "You do. Hand the document to the traveller yourself before the flight — or have someone you trust do the handover for you. At the destination, your receiver collects it directly from the traveller. The traveller only carries the document during the journey.",
    },
    {
      q: "How will I know where my document is?",
      a: "Your TurantX dashboard keeps you updated throughout the journey—from matched, handed over, and in transit to delivered. We'll also notify you via SMS and TurantChat at important milestones.",
    },
  ],
  faqsTraveller: [
    {
      q: "How does it work for me?",
      a: "Share your route, travel date, and available luggage space. Once your flight ticket and travel details are verified, you're added to our verified traveller network. When there's a matching request on your route, we'll contact you—you can accept or decline.",
    },
    {
      q: "How much and when do I get paid?",
      a: "You can earn ₹200–₹800 per successful trip, with the amount shared before you accept. Payment is transferred to your registered UPI after the receiver confirms delivery using the Delivery OTP.",
    },
    {
      q: "What exactly will I carry—is it safe for me?",
      a: "You're always in control. You'll only carry document envelopes such as passports, contracts, medical reports, and other approved documents. No cash, valuables, or unknown packages. Every request is reviewed by our team before it reaches you, you inspect the document before accepting it, and you're free to decline any request without penalty.",
    },
    {
      q: "Do I have to do pickup and drop?",
      a: "No. The sender brings the document to you before your flight, and the receiver collects it after you land. Your only responsibility is carrying the document during your journey.",
    },
    {
      q: "What if my flight gets delayed or cancelled?",
      a: "Simply let us know through TurantChat or SMS. There are no penalties. We'll inform the sender and arrange another traveller if required.",
    },
  ],
  whyBenefits: {
    sender: [
      { title: "Hours, not days — even for urgent documents", desc: "Couriers need warehouses, sorting hubs, and a 2-day window minimum. TurantX sends your document on the next available flight, carried by someone already going there." },
      { title: "No pickup windows. No waiting.", desc: "Post your cities and what you're sending. Our team finds and verifies a match. You bring the document to the traveller before their flight — yourself, or through someone you trust. That's it." },
    ],
    traveller: [
      { title: "Earn ₹200–₹800 per trip, on flights you're already taking", desc: "You're already on that flight. Carrying a small envelope takes 5 minutes of extra effort. Payment goes to your UPI once the receiver collects the document at the destination." },
      { title: "Sender handles everything around the flight", desc: "You don't run any errands. The sender brings the document to you before the flight. Their receiver collects it at the destination. Your only job is to carry it on board." },
    ],
    notes: [
      "First delivery free — a fee may apply from the second onwards, shared upfront.",
      "Travellers are paid only after a confirmed handover at the destination.",
      "We're a small team, currently in five cities across India.",
    ],
  },
  safetyCards: [
    { title: "Travellers are flight-checked", desc: "We verify the traveller's flight booking before they can be matched with any sender." },
    { title: "Both sides are ID-checked", desc: "Travellers and senders both go through a government ID check before any match is made." },
    { title: "A real person reviews every match", desc: "No algorithm decides. A person on our team checks every match before contact details are shared." },
    { title: "Documents only — no exceptions", desc: "We accept passports, contracts, legal papers, cheques, and medical documents. No cash, jewellery, electronics, or anything banned by airlines or Indian law." },
  ],
  pages: {
    about: {
      tagline: "Connecting senders with verified travellers — not a courier company.",
      lead: "TurantX is a matching platform. We connect senders with verified travellers who are already flying the same route. We do not move parcels ourselves — we make sure the people and the route are real.",
      values: [
        { icon: "🤝", title: "Matching, not delivery", desc: "We connect senders with travellers flying same route. Both pickup and delivery are OTP authenticated, and we verify both parties before anything is shared." },
        { icon: "🔐", title: "Manual verification", desc: "Both senders and travellers are manually verified. Traveller IDs and PNRs are checked before every match is approved." },
        { icon: "🟢", title: "First delivery free", desc: "Your first delivery is free. From the second onwards a delivery fee may apply, shared with you before the delivery is arranged. If we can't find a verified match, your request is simply cancelled." },
      ],
      storyHeading: "",
      storyParagraphs: [
        "Every day, important documents travel through delivery networks built for scale, not urgency. Along the way, they often pass through multiple sorting and handling points, adding time, complexity, and the possibility of delays or damage.",
        "At the same time, thousands of travellers are already flying those very routes with available luggage space. We saw an opportunity to rethink document delivery—not by building another courier network, but by creating a trusted platform that connects verified senders with verified travellers already making the journey.",
        "Trust is at the heart of TurantX. Every sender and traveller is manually verified, every match is carefully reviewed before approval, and both pickup and delivery are secured with OTP verification. By using journeys that are already happening, we're making urgent document delivery simpler, faster, and more secure—one trusted handover at a time.",
      ],
      missionText: "Build a peer-to-peer document delivery service that is honest, verified, and worth trusting — even when that means saying \"we could not match you this time\".",
    },
    "how-it-works": {
      title: "How TurantX Works (Pilot Phase)",
      body: "1) Sender posts a request\nShare your pickup city, destination city, and a short description of the document. Our team will reach out to you via SMS to take it forward.\n\n2) Traveller signs up with flight details\nTravellers share their route and flight booking. We do a flight check and ID check before any match.\n\n3) Our team reviews the match manually\nEvery potential match is checked by a real person on our team. If something does not look right, we cancel.\n\n4) Sender brings the document to the traveller\nThis is the sender's job — not TurantX's. Meet the traveller before their flight in person, or have someone you trust do the handover for you. The traveller only carries it on the flight; they do not arrange any pickup or drop.\n\n5) Receiver collects at the destination\nThe sender's receiver — whoever is collecting the document on the other end — picks it up directly from the traveller. The sender arranges this meetup. TurantX shares both sides' contact details via SMS once the match is confirmed.\n\n6) No match? No charges.\nIf we cannot find a traveller on your route, the request is cancelled. There is no fee for senders — no money is collected or held.",
    },
    "why-turantx": {
      title: "Useful for both sides.",
      body: "",
    },
    help: {
      title: "Help & Support",
      body: "",
    },
    contact: {
      title: "Contact Us",
      body: "Email: support@turantx.com\nPhone: +91 98765-45520\nBased in India.",
    },
    safety: {
      title: "How we keep things safe",
      body: "",
    },
    terms: {
      title: "Terms & Conditions",
      body: "Last updated: 16-Oct-2026\n\nThese are the terms for using TurantX. By using the app or website, you agree to them. If you don't agree, please don't use the service.\n\n1. What TurantX is\nTurantX is a matching platform. We connect senders with verified flight travellers for urgent document delivery. We are not a courier company. We do not move, store, or handle documents ourselves.\n\n2. We are in pilot phase\nTurantX is in an early pilot. Features, availability, and processes may change. Some features — like doorstep pickup or delivery — are not available yet.\n\n3. Who can use TurantX\nYou must be at least 18 years old.\nYou must give us accurate information.\nTravellers must complete ID verification before being matched.\n\n4. What you can send\nDocuments only — passports, contracts, legal papers, medical reports, cheques.\nNot allowed: cash, jewellery, electronics, drugs, weapons, perishables, or anything banned by airlines or Indian law.\nWe will cancel any request that involves prohibited items.\n\n5. What TurantX does and does not do\nWe match senders with travellers and verify both sides.\nWe do not transport, touch, or guarantee documents.\nWe do not promise same-day or on-time delivery.\n\n6. Sender's responsibilities\nAs a sender, you agree to:\nGive correct pickup and destination details.\nSend only legal, non-restricted documents.\nHandle the first mile yourself — bring the document to the traveller before their flight, in person or through someone you trust.\nArrange the last mile yourself — your receiver must collect the document from the traveller at the destination.\nCoordinate directly with the traveller using contact details shared by TurantX.\n\n7. Traveller's responsibilities\nAs a traveller, you agree to:\nCarry only documents that TurantX has approved for the trip.\nHandle documents carefully and hand them to the receiver as agreed.\nFollow all airline rules, airport security rules, and Indian law.\nRefuse any package that looks suspicious or unsafe.\n\n8. Payments\nSenders pay nothing for their first delivery. From the second delivery onwards, a delivery fee may apply — the amount is shared with you before the delivery is arranged.\nTravellers may earn a fee per trip — amount is shared upfront before they accept a match.\nWe may run route-specific campaigns that offer a guaranteed reward to first-time travellers on that route. Where such a campaign applies, the guaranteed reward is payable even if no match is found, subject to the conditions shown on that campaign's page. An unmatched guaranteed reward is paid within 7 days of the flight date, once we confirm the flight was taken. We will contact you to collect your payment details.\nFuture pricing may change with advance notice.\n\n9. If no match is found\nIf we cannot find a traveller for your route, the request is cancelled.\nNo payment is collected from senders, so no refund applies.\nIf you are a traveller who qualifies for a route campaign's guaranteed reward, that reward remains payable even though no match was found.\n\n10. Our liability\nTurantX is not liable for loss, damage, delay, or misuse of documents.\nDelivery happens between two independent people — we only facilitate the match.\nTo the extent the law allows, our liability is limited to any fees actually paid to us.\n\n11. Verification\nTravellers are government ID-verified and flight-checked.\nEvery match is reviewed by a real person on our team.\nThis reduces risk but does not eliminate it — peer delivery carries some inherent risk, and you acknowledge that by using the service.\n\n12. Account suspension\nWe can suspend or remove accounts if:\nFalse information is given.\nThese terms are broken.\nSuspicious or unsafe activity is detected.\n\n13. Privacy\nYour data is handled as described in our Privacy Policy. By using TurantX, you agree to how we collect and use your information.\n\n14. Changes to these terms\nWe may update these terms. If we do, we'll update the date above. Continued use of TurantX after changes means you accept the new terms.\n\n15. Governing law\nThese terms are governed by Indian law. Any disputes will be handled in Indian courts.\n\n16. Contact\n📧 support@turantx.com",
    },
    privacy: {
      title: "Privacy Policy",
      body: "Last updated: 10-May-2026\n\nThis is a plain-English summary of how TurantX handles your data during the pilot. We've kept it short and honest. The legal version lives in our Terms — this is the human one.\n\n1. What we collect\nWhen you sign up: your name, mobile number, and a government ID for verification.\nWhen you post a request (sender): pickup and destination cities, and a short description of what you're sending.\nWhen you sign up as a traveller: your route, flight date, and flight booking details so we can verify the flight.\nWe also collect basic device and usage info — same as any normal website.\n\n2. Why we collect it\nTo verify both sides before any match.\nTo connect a sender with the right traveller.\nTo reach you on your registered mobile number when there's an update on your request.\nTo respond if you write to support.\n\n3. Who we share it with\nThe person you're matched with — and only after both sides clear our checks. We share name, route, and contact number. Nothing more.\nOur small ops team, who manually review every match.\nThat's it. We don't sell your data. We don't share it with advertisers. We don't run ad tracking on this site.\n\n4. Where it lives\nYour data is stored on Google Firebase (an industry-standard cloud platform). It's encrypted in transit and at rest.\n\n5. How long we keep it\nFor active accounts: as long as you're using TurantX.\nFor cancelled or completed requests: up to 18 months, after which we delete the request data. Account-level data (name, phone) we keep until you ask us to remove it.\n\n6. Your rights\nYou can ask us to show you what we have on you, correct it, or delete it. Email support@turantx.com and we'll act within 7 days.\n\n7. Cookies\nWe use a few simple cookies — to keep you logged in, to remember your preferences, and to detect basic abuse. We don't use advertising or behavioural-tracking cookies.\n\n8. Changes\nIf this policy changes, we'll update the date above and tell active users on their registered number before any significant change takes effect.\n\n9. Contact\nQuestions or requests? Email support@turantx.com. We're a small team; you'll hear back from a real person.",
    },
  },
};

function isValidShape(c) {
  return (
    c &&
    typeof c === "object" &&
    c.hero && typeof c.hero === "object" &&
    Array.isArray(c.testimonials)
  );
}

function isObj(v) { return v && typeof v === "object" && !Array.isArray(v); }

function mergePagesDefaults(remote) {
  const def = DEFAULT_CONTENT.pages;
  if (!isObj(remote)) return def;
  const result = { ...def };

  // About — structured fields
  if (isObj(remote.about)) {
    const da = def.about;
    const ra = remote.about;
    result.about = {
      tagline: typeof ra.tagline === "string" ? ra.tagline : da.tagline,
      lead: typeof ra.lead === "string" ? ra.lead : da.lead,
      values: Array.isArray(ra.values) && ra.values.length === 3
        ? ra.values.map((v, i) => ({ ...da.values[i], ...(isObj(v) ? v : {}) }))
        : da.values,
      storyHeading: typeof ra.storyHeading === "string" ? ra.storyHeading : da.storyHeading,
      storyParagraphs: Array.isArray(ra.storyParagraphs) && ra.storyParagraphs.length === 3
        ? ra.storyParagraphs.map((p, i) => typeof p === "string" ? p : da.storyParagraphs[i])
        : da.storyParagraphs,
      missionText: typeof ra.missionText === "string" ? ra.missionText : da.missionText,
    };
  }

  // Prose pages — title + body
  for (const key of ["how-it-works", "why-turantx", "help", "contact", "safety", "terms", "privacy"]) {
    if (isObj(remote[key])) {
      result[key] = {
        title: typeof remote[key].title === "string" ? remote[key].title : def[key].title,
        body: typeof remote[key].body === "string" ? remote[key].body : def[key].body,
      };
    }
  }

  return result;
}

function mergeWithDefaults(remote) {
  if (!isValidShape(remote)) return DEFAULT_CONTENT;
  return {
    hero: { ...DEFAULT_CONTENT.hero, ...remote.hero },
    testimonials: remote.testimonials.length ? remote.testimonials : DEFAULT_CONTENT.testimonials,
    faqs: Array.isArray(remote.faqs) ? remote.faqs : DEFAULT_CONTENT.faqs,
    faqsSender: Array.isArray(remote.faqsSender) && remote.faqsSender.length
      ? remote.faqsSender
      : DEFAULT_CONTENT.faqsSender,
    faqsTraveller: Array.isArray(remote.faqsTraveller) && remote.faqsTraveller.length
      ? remote.faqsTraveller
      : DEFAULT_CONTENT.faqsTraveller,
    safetyCards: Array.isArray(remote.safetyCards) && remote.safetyCards.length
      ? remote.safetyCards
      : DEFAULT_CONTENT.safetyCards,
    whyBenefits: isObj(remote.whyBenefits)
      ? {
          sender: Array.isArray(remote.whyBenefits.sender) && remote.whyBenefits.sender.length
            ? remote.whyBenefits.sender : DEFAULT_CONTENT.whyBenefits.sender,
          traveller: Array.isArray(remote.whyBenefits.traveller) && remote.whyBenefits.traveller.length
            ? remote.whyBenefits.traveller : DEFAULT_CONTENT.whyBenefits.traveller,
          notes: Array.isArray(remote.whyBenefits.notes) && remote.whyBenefits.notes.length
            ? remote.whyBenefits.notes : DEFAULT_CONTENT.whyBenefits.notes,
        }
      : DEFAULT_CONTENT.whyBenefits,
    pricing: isObj(remote.pricing)
      ? { ...DEFAULT_CONTENT.pricing, ...remote.pricing }
      : DEFAULT_CONTENT.pricing,
    cities: Array.isArray(remote.cities) && remote.cities.length
      ? remote.cities.filter((c) => typeof c === "string" && c.trim()).map((c) => c.trim())
      : DEFAULT_CONTENT.cities,
    stats: Array.isArray(remote.stats) && remote.stats.length === 3
      ? remote.stats.map((s, i) => ({ ...DEFAULT_CONTENT.stats[i], ...(isObj(s) ? s : {}) }))
      : DEFAULT_CONTENT.stats,
    banner: isObj(remote.banner)
      ? normalizeBanner({ ...DEFAULT_CONTENT.banner, ...remote.banner }, remote.banner)
      : DEFAULT_CONTENT.banner,
    banner2: isObj(remote.banner2)
      ? normalizeBanner({ ...DEFAULT_CONTENT.banner2, ...remote.banner2 }, remote.banner2)
      : DEFAULT_CONTENT.banner2,
    pages: mergePagesDefaults(remote.pages),
  };
}

const BANNER_AUDIENCES = ["traveller", "sender", "both"];
// Banners saved before the audience field existed have none. A banner built
// from campaign placeholders ({from} {reward} …) is the traveller route
// campaign, so it becomes a traveller banner; anything else keeps showing to
// everyone, exactly as it did before.
const normalizeBanner = (merged, raw) => {
  if (BANNER_AUDIENCES.includes(raw.audience)) return merged;
  const usesCampaign = /\{(from|to|reward|slug|fromCode|toCode)\}/.test(`${raw.text || ""} ${raw.link || ""}`);
  return { ...merged, audience: usesCampaign ? "traveller" : "both" };
};

export async function loadContent({ skipCache = false, withMeta = false } = {}) {
  if (!skipCache) {
    const cached = readCache();
    if (cached) return withMeta ? { data: cached, source: "cache" } : cached;
  }
  const TIMEOUT_MS = 6000;
  const fetchWithTimeout = async () => {
    const url = await getDownloadURL(ref(storage, STORAGE_PATH));
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const raw = await res.json();
    console.log(
      "[contentService] fetched",
      Array.isArray(raw?.testimonials) ? raw.testimonials.length : "?",
      "testimonials,",
      Array.isArray(raw?.faqsSender) ? raw.faqsSender.length : "?",
      "sender FAQs,",
      Array.isArray(raw?.faqsTraveller) ? raw.faqsTraveller.length : "?",
      "traveller FAQs"
    );
    return mergeWithDefaults(raw);
  };
  try {
    const data = await Promise.race([
      fetchWithTimeout(),
      new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), TIMEOUT_MS)),
    ]);
    writeCache(data);
    return withMeta ? { data, source: "storage" } : data;
  } catch (err) {
    console.error("[contentService.loadContent] failed:", err?.code || err?.message || err);
    const cached = readCache();
    if (cached) {
      console.warn("[contentService.loadContent] using stale localStorage cache as fallback");
      return withMeta ? { data: cached, source: "cache-stale", error: err } : cached;
    }
    console.warn("[contentService.loadContent] no cache available — using DEFAULT_CONTENT");
    return withMeta ? { data: DEFAULT_CONTENT, source: "default", error: err } : DEFAULT_CONTENT;
  }
}

export async function saveContent(content) {
  const TIMEOUT_MS = 12000;
  const safe = mergeWithDefaults(content);
  const json = JSON.stringify(safe, null, 2);
  const upload = uploadString(ref(storage, STORAGE_PATH), json, "raw", {
    contentType: "application/json",
    cacheControl: "public, max-age=300, must-revalidate",
  });
  await Promise.race([
    upload,
    new Promise((_, reject) =>
      setTimeout(
        () => reject(new Error("Upload timed out after 12s — Firebase Storage rules may not be set, or Storage isn't enabled in your Firebase project. Check Firebase Console → Storage.")),
        TIMEOUT_MS
      )
    ),
  ]);
  clearCache();
  writeCache(safe);
  return safe;
}
